import React, {useEffect, useMemo, useRef, useState} from "react";
import {Calendar, Views, momentLocalizer} from "react-big-calendar";
import moment from "moment";
import {useTranslation} from "react-i18next";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "./index.scss";

const localizer = momentLocalizer(moment);

function EventItem({event}) {
	const isMatch = event.__eventType === "match";

	if (isMatch) {
		const home = event.homeClub || "---";
		const away = event.awayClub || "---";
		const hasResult =
			Number.isInteger(event.homeClubGoals) &&
			Number.isInteger(event.awayClubGoals);
		const result = hasResult
			? `${event.homeClubGoals} - ${event.awayClubGoals}`
			: event.composedResult || "";
		const ha = typeof event.away === "boolean" ? (event.away ? "T" : "C") : "";

		return (
			<div className="rbc-event-content">
				<span className="evt-line">
					{ha && <span className="evt-home-away">{ha}</span>}
					<span className="evt-teams">
						{/* <strong>{home}</strong> - <strong>{away}</strong> */}
						<strong>{event.playersCategory || "Match"}</strong>
					</span>
				</span>
				{/* {result && <span className="evt-sub">{result}</span>} */}
			</div>
		);
	}

	// Training event - show only essential info
	const time = event.start ? moment(event.start).format("HH:mm") : "";
	return (
		<div className="rbc-event-content">
			<span className="evt-line">
				<strong>
					{event.playersCategory || event._t_training || "Training"}
				</strong>
				{time && <span className="evt-time">{time}</span>}
			</span>
		</div>
	);
}

export default function EventsCalendar(props) {
	const {
		events = [],
		view = Views.MONTH,
		onSelectEvent,
		onNavigate,
		onView,
		date,
	} = props;

	const {t} = useTranslation();

	const containerRef = useRef(null);
	const [calHeight, setCalHeight] = useState(600);

	useEffect(() => {
		const computeHeight = () => {
			if (!containerRef.current) return;
			const rect = containerRef.current.getBoundingClientRect();
			const top = rect.top + window.scrollY;
			const viewportH = window.innerHeight;
			const paddingBottom = 16;
			const h = Math.max(400, Math.floor(viewportH - rect.top - paddingBottom));
			setCalHeight(h);
		};
		computeHeight();
		window.addEventListener("resize", computeHeight);
		window.addEventListener("orientationchange", computeHeight);
		return () => {
			window.removeEventListener("resize", computeHeight);
			window.removeEventListener("orientationchange", computeHeight);
		};
	}, []);

	const rbcEvents = useMemo(() => {
		return (events || []).map((e) => {
			const start = new Date(e.startDate);
			let end = e.endDate ? new Date(e.endDate) : new Date(e.startDate);

			// Normalize dates to same day to prevent multi-day events
			const startDay = new Date(
				start.getFullYear(),
				start.getMonth(),
				start.getDate(),
			);
			const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());

			// If end is on a different day, set it to end of start day
			if (endDay.getTime() !== startDay.getTime()) {
				end = new Date(startDay);
				end.setHours(23, 59, 59, 999);
			}

			// Ensure end is after start for proper rendering (minimum 1 hour)
			if (end <= start) {
				end = new Date(start.getTime() + 60 * 60 * 1000);
			}

			return {
				...e,
				start,
				end,
				title:
					e.__eventType === "match"
						? e.opponentTeamName || "Match"
						: e.fieldName ||
							e.playersCategory ||
							t("training", {defaultValue: "Training"}),
				_t_training: t("training", {defaultValue: "Training"}),
				allDay: false,
			};
		});
	}, [events, t]);

	const eventPropGetter = (event) => {
		const isMatch = event.__eventType === "match";
		const style = {
			backgroundColor: isMatch ? "#1976d2" : "#2e7d32",
			borderColor: isMatch ? "#1565c0" : "#1b5e20",
			color: "#fff",
		};
		return {style};
	};

	const minTime = useMemo(() => {
		const d = new Date();
		d.setHours(8, 0, 0, 0);
		return d;
	}, []);
	const maxTime = useMemo(() => {
		const d = new Date();
		d.setHours(23, 0, 0, 0);
		return d;
	}, []);

	return (
		<div className="events-calendar-container" ref={containerRef}>
			<Calendar
				style={{height: calHeight + "px", width: "100%"}}
				localizer={localizer}
				events={rbcEvents}
				startAccessor="start"
				endAccessor="end"
				view={view}
				onView={onView}
				views={[Views.MONTH, Views.WEEK]}
				onSelectEvent={onSelectEvent}
				eventPropGetter={eventPropGetter}
				onNavigate={onNavigate}
				date={date}
				components={{event: EventItem}}
				min={minTime}
				max={maxTime}
				messages={{
					today: t("calendar_today", {defaultValue: "Today"}),
					previous: t("calendar_previous", {defaultValue: "Back"}),
					next: t("calendar_next", {defaultValue: "Next"}),
					month: t("calendar_month", {defaultValue: "Month"}),
					week: t("calendar_week", {defaultValue: "Week"}),
					day: t("calendar_day", {defaultValue: "Day"}),
					agenda: t("calendar_agenda", {defaultValue: "Agenda"}),
					showMore: (total) =>
						t("calendar_show_more", {
							count: total,
							defaultValue: "+${total} more",
						}).replace("${total}", total),
				}}
				popup
			/>
		</div>
	);
}
